/** Mirrors the `UserRole` enum in the Prisma schema. Keep the two in sync. */
export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  PLATFORM_SUPPORT: 'PLATFORM_SUPPORT',
  ADMIN: 'ADMIN',
  PRINCIPAL: 'PRINCIPAL',
  TEACHER: 'TEACHER',
  STUDENT: 'STUDENT',
  PARENT: 'PARENT',
  ACCOUNTANT: 'ACCOUNTANT',
  LIBRARIAN: 'LIBRARIAN',
  COUNSELLOR: 'COUNSELLOR',
  DRIVER: 'DRIVER',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

/**
 * The accounts a class may be allotted to.
 *
 * Teachers, and principals — who take a period or two in most Indian institutes.
 * Mirrors `TEACHING_ROLES` in the backend's allocations service, which is the
 * one that decides. Lives here rather than in either feature because both the
 * staff screen (who gets a Classes button) and the teacher console (whose
 * classes are worth asking for) read it.
 */
export const TEACHING_ROLES: Role[] = [ROLES.TEACHER, ROLES.PRINCIPAL];

export const ROLE_LABEL: Record<Role, string> = {
  SUPER_ADMIN: 'Super administrator',
  PLATFORM_SUPPORT: 'Platform support',
  ADMIN: 'Administrator',
  PRINCIPAL: 'Principal',
  TEACHER: 'Teacher',
  STUDENT: 'Student',
  PARENT: 'Parent',
  ACCOUNTANT: 'Accountant',
  LIBRARIAN: 'Librarian',
  COUNSELLOR: 'Counsellor',
  DRIVER: 'Driver',
};

/**
 * Where each role lands after signing in. Every role gets its own dashboard,
 * so this is the one place that decides the post-login destination.
 */
export const ROLE_HOME: Record<Role, string> = {
  // Platform staff land in our own console, never in an institute.
  SUPER_ADMIN: '/platform',
  PLATFORM_SUPPORT: '/platform',
  ADMIN: '/admin',
  PRINCIPAL: '/principal',
  TEACHER: '/teacher/dashboard',
  STUDENT: '/student',
  PARENT: '/parent',
  ACCOUNTANT: '/accounts',
  LIBRARIAN: '/library',
  COUNSELLOR: '/counsellor',
  // Drivers work from the mobile app. On the web they land on the placeholder
  // dashboard until a driver console exists.
  DRIVER: '/driver',
};

/**
 * The first segment of every role's console — `/admin`, `/student`, `/teacher`.
 *
 * Derived from `ROLE_HOME` rather than written out again, so a role that moves
 * house cannot leave a stale copy behind.
 */
const ROLE_AREAS = new Set(Object.values(ROLE_HOME).map(areaOf));

/** `/teacher/dashboard` → `/teacher`. Empty for `/`. */
function areaOf(path: string): string {
  const [, first = ''] = path.split('/');
  return first ? `/${first}` : '';
}

/**
 * Whether somebody of this role may be sent to this path.
 *
 * Used for the "back where you were headed" redirect after signing in. Without
 * it, whatever address bounced somebody to the login screen decides where they
 * land — so an administrator who arrived at a stale `/student` tab signs in and
 * is dropped into the student panel, which is the one place their account has
 * no business being.
 *
 * A path in nobody's console — a shared review screen, a print view — is
 * allowed through: those are reached by people of several roles, and the route
 * itself is still guarded. This only refuses *another role's* console.
 */
export function isPathForRole(path: string, role: Role): boolean {
  const area = areaOf(path);
  if (!area || !ROLE_AREAS.has(area)) return true;
  return area === areaOf(ROLE_HOME[role]);
}
